/* eslint-disable react/require-default-props */
import React, { FC, Dispatch, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import { PersonalizationTagTypes } from '@btag/bt-personalization';
import { TAppState } from '../../../../../reducers';
import eventService from '../../../../../common/eventService';
import { ERR_PERSONALIZATION_TAG_SET } from '../../../../../constants/Errors';
import { IPersonalizationTagDefault } from '../../../../../types/personalization';
import { setTag, clearErrors } from '../../../../../actions/personalization';
import BananatagButton from '../../../../../componentLibrary/BananatagButton';
import Tooltip from '../../../../../componentLibrary/Tooltip';
import Input from '../../../../../componentLibrary/Input';
import { Modal } from '../../../../../componentLibrary';
import * as Styled from './styles';

interface IProps {
    tag: IPersonalizationTagDefault;
    setIsEditPopUpOpen: (isOpen: boolean) => void;
    draftID: string;
    isSetTagLoading: boolean;
    hasError: boolean;
    dispatch: Dispatch<any>;
}

const TagEdit: FC<IProps> = ({ tag, setIsEditPopUpOpen, draftID, isSetTagLoading, hasError, dispatch }) => {
    const { fallback, name } = tag;
    const [newFallback, setNewFallback] = useState(fallback);
    const [isUpdatingTag, setIsUpdatingTag] = useState(false);

    // close pop up after tag update
    useEffect(() => {
        if (isUpdatingTag && !isSetTagLoading) {
            setIsUpdatingTag(false);
            setIsEditPopUpOpen(false);
        }
    }, [isUpdatingTag, isSetTagLoading]);

    useEffect(() => {
        if (hasError) {
            eventService.notify(ERR_PERSONALIZATION_TAG_SET, true);
            dispatch(clearErrors());
        }
    }, [hasError]);

    return (
        <Modal
            dataTestId={`tag-edit-${name}`}
            width={272}
            height={212}
            padding={24}
            borderRadius={4}
            onEsc={() => setIsEditPopUpOpen(false)}
        >
            {isSetTagLoading ? (
                <Styled.LoadingImg
                    data-test-id="tag-edit-loading-spinner"
                    width="42"
                    src="https://app-data-legacy.bananatag.com/pikasso/assets/loader/banana-loader.gif"
                    alt="loader"
                />
            ) : (
                <>
                    <Styled.TitleWrapper>
                        <Styled.TitleText>Fallback Text</Styled.TitleText>
                        <Tooltip
                            id="tag-edit-icon-tooltip"
                            text={`If there is a missing {{${name}}} value for a recipient we will use this fallback instead`}
                            width="152px"
                            padding="22px"
                            place="top"
                            textLineHeight="20px"
                        >
                            <Styled.Icon icon="question-circle" />
                        </Tooltip>
                    </Styled.TitleWrapper>
                    <Input
                        type="text"
                        width={224}
                        value={newFallback}
                        maxLength={120}
                        label={`{{${name}}} fallback`}
                        labelFontWeight={400}
                        id={`tag-fallback-edit-${name}`}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const { value } = e.target;
                            setNewFallback(value);
                        }}
                    />
                    <Styled.Text>
                        Only tags <b>in this email</b> will use this fallback text.
                    </Styled.Text>
                    <Styled.ButtonWrapper>
                        <BananatagButton
                            text="cancel"
                            onClick={() => setIsEditPopUpOpen(false)}
                            type="clear"
                            size="small"
                            styling={{
                                borderRadius: '4px',
                            }}
                        />
                        <BananatagButton
                            text="apply"
                            onClick={() => {
                                dispatch(
                                    setTag({
                                        ...tag,
                                        draftID,
                                        name,
                                        fallback: newFallback,
                                        type: PersonalizationTagTypes.DEFAULT,
                                    })
                                );
                                setIsUpdatingTag(true);
                            }}
                            type="solid"
                            size="small"
                            disabled={isSetTagLoading || fallback === newFallback}
                            styling={{
                                borderRadius: '4px',
                            }}
                        />
                    </Styled.ButtonWrapper>
                </>
            )}
        </Modal>
    );
};

const mapStateToProps = (state: TAppState) => ({
    draftID: _get(state, 'session.collaboration.id', ''),
    isSetTagLoading: _get(state, 'personalization.tag.isSetTagLoading', true),
    hasError: !!_get(state, 'personalization.tag.setTagError', ''),
});

export default connect(mapStateToProps)(TagEdit);
